import { GroupType } from '@bp/bp-graphql-types';
import { GroupRoles } from '@bp/pim-auth-constants';
import { ArrowLeftIcon, ArrowRightIcon, ModalBottomButtons } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { splitProfilesByOrganization } from 'utils/splitProfilesByOrganization';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { CollaborationAdminsValues, GroupMember } from '../types/types';
import { GroupBaseDataForm } from './GroupBaseDataForm';
import { GroupCollaborationForm } from './GroupCollaborationForm';
import styles from './GroupForm.module.scss';

const CreateGroupsMutationDocument = graphql(`
  mutation BpCreateGroupsForm($input: BpGroupCreateInput!) {
    createBpGroup(input: $input) {
      __typename
      uuid
    }
  }
`);

export const UpdateGroupsMutationDocument = graphql(`
  mutation BpUpdateGroupsForm($update: BpGroupUpdateInput!, $uuid: ID!) {
    updateBpGroup(update: $update, uuid: $uuid) {
      __typename
      uuid
    }
  }
`);

const GroupFormPageQueryDocument = graphql(`
  query GroupFormPageData($groupUuid: ID!, $organizationUuid: ID!) {
    groups(where: { uuid: $groupUuid }) {
      __typename
      uuid
      name
      isCommunity
      schoolYear {
        __typename
        uuid
      }
      admins {
        __typename
        uuid
        displayName
        firstName
        lastName
        organizationRoles
        organization {
          __typename
          uuid
        }
      }
      editors {
        __typename
        uuid
      }
      viewers {
        __typename
        uuid
      }
    }
    profiles(where: { organization: { uuid: $organizationUuid } }, options: { sort: { selectName: ASC } }) {
      __typename
      uuid
      selectName
      organizationRoles
    }
    ...GroupBaseDataFormFields
  }
`);

export type GroupFormType = {
  name: string;
  schoolYear: string | null;
  admins: GroupMember[];
  collaborationSchools: string[];
  collaborationAdmins: CollaborationAdminsValues;
  collaborating: boolean;
};

export type GroupFormProps = {
  groupUuid: string | null;
  schoolYearUuid: string;
  page: 'groups' | 'institution';
  onStep: (step: number) => void;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const GroupForm = ({ groupUuid, schoolYearUuid, page, onStep, onLoad, onClose }: GroupFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organization = pimAuthClaims.getOrganization();
  const profile = pimAuthClaims.getProfile();
  const profileUuid = profile.uuid;

  const [{ data }] = useQuery({
    query: GroupFormPageQueryDocument,
    variables: {
      organizationUuid: organization.uuid,
      groupUuid: groupUuid ?? '',
      schoolYearUuid: schoolYearUuid,
    },
  });

  const currentGroup = data?.groups[0];

  const isGroupsPage = page === 'groups';

  const [step, setStep] = useState<number>(0);
  const [maxSteps, setMaxSteps] = useState<number>(0);
  const [modalError, setModalError] = useState<string | null>(null);
  const [isCollaborating, setIsCollaborating] = useState<boolean>(false);

  const [, createGroup] = useMutation(CreateGroupsMutationDocument);
  const [, updateGroup] = useMutation(UpdateGroupsMutationDocument);

  const handleSubmit = async (values: GroupFormType, helpers: FormikHelpers<GroupFormType>) => {
    const errors = validate(values);
    if (Object.keys(errors).length > 0) return;

    onLoad(true);

    const { name, schoolYear, admins, collaborationAdmins, collaborating } = values;

    const adminUuids = admins.map((a) => a.profileUuid);
    const collaborationAdminUuids = Object.values(collaborationAdmins)
      .flatMap((v) => v?.map((item) => item.uuid))
      .filter((item) => item);
    const uniqueAdminUuids = Array.from(new Set([...adminUuids, ...collaborationAdminUuids]));

    const input = {
      name,
      schoolYear: schoolYear ?? undefined,
      hasMembersFrom: [],
      admins: uniqueAdminUuids,
      editors: currentGroup?.editors.map((e) => e.uuid) ?? [],
      viewers: currentGroup?.viewers.map((v) => v.uuid) ?? [],
    };

    if (groupUuid) {
      const { error } = await updateGroup({
        update: input,
        uuid: groupUuid,
      });
      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessUpdateToast();
      }
    } else {
      const { error } = await createGroup({
        input: {
          organization: pimAuthClaims.getOrganizationUuid(),
          groupType: GroupType.Group,
          foreignRefId: [],
          isCommunity: collaborating,
          ...input,
        },
      });
      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    }

    onClose();
    helpers.resetForm();
    onLoad(false);
  };

  const otherOrganizations = currentGroup?.admins
    ? splitProfilesByOrganization(
        currentGroup.admins.map((a) => ({
          uuid: a.uuid,
          organization: a.organization,
          displayName: a.displayName ?? '',
        })),
        organization.uuid,
      )
    : null;

  const admins: GroupMember[] =
    currentGroup?.admins
      .filter((admin) => admin.organization.uuid === organization.uuid)
      .map((admin) => {
        return {
          profileUuid: admin.uuid,
          groupRole: GroupRoles.Admin,
          organizationRoles: admin.organizationRoles,
          name: `${admin.lastName}, ${admin.firstName}`,
        };
      }) ??
    (isGroupsPage
      ? [
          {
            profileUuid: profileUuid,
            groupRole: GroupRoles.Admin,
            organizationRoles: ['TEACHER'], // hacky
            name: profile.displayName ?? '',
          },
        ]
      : []);

  const initialValues: GroupFormType = {
    name: currentGroup?.name ?? '',
    schoolYear: currentGroup?.schoolYear?.uuid ?? null,
    admins,
    collaborationAdmins: otherOrganizations ?? {},
    collaborationSchools: otherOrganizations ? Object.keys(otherOrganizations) : [],
    collaborating: !!otherOrganizations || (currentGroup?.isCommunity ?? false),
  };

  function handleCollaborationChange(isCollaboration: boolean) {
    setIsCollaborating(isCollaboration);
    setMaxSteps(isCollaboration && !isGroupsPage ? 1 : 0);
  }

  function validate(values: GroupFormType) {
    const errors: FormikErrors<GroupFormType> = {};
    setModalError(null);

    if (step === 0) {
      if (!values.name || values.name === '') {
        errors.name = t('validation.required.designation');
      }
      if (!values.admins || values.admins.length === 0) {
        errors.admins = t('validation.required.admins');
      }
    }

    if (step === 1) {
      if (values.collaborating && values.collaborationSchools.length === 0) {
        setModalError(t('validation.required.collaborationSchools'));
        errors.collaborationSchools = t('validation.required.collaborationSchools');
      }
      if (values.collaborationSchools.length > 0) {
        values.collaborationSchools.forEach((schoolUuid) => {
          const admins = values.collaborationAdmins[schoolUuid];
          if (!admins || admins.length === 0) {
            if (!errors.collaborationAdmins) {
              errors.collaborationAdmins = {};
            }
            errors.collaborationAdmins[schoolUuid] = t('validation.required.collaborationAdmins');
          }
        });
      }
    }

    return errors;
  }

  useEffect(() => {
    setMaxSteps(!!otherOrganizations && !isGroupsPage ? 1 : 0);
  }, [isGroupsPage, otherOrganizations]);

  useEffect(() => {
    onStep(step);
  }, [onStep, step]);

  return (
    <Formik<GroupFormType> onSubmit={handleSubmit} initialValues={initialValues} validate={validate}>
      {({ errors, validateForm }) => (
        <Form className={styles['group-form']}>
          <div className={styles.slider}>
            <div
              tabIndex={0}
              className={styles.slide}
              style={{ left: step === 0 ? '0' : step > 0 ? `${step * -100}%` : `${step * 100}%` }}
            >
              <GroupBaseDataForm page={page} onCollaborationChange={handleCollaborationChange} query={data} />
            </div>

            <div
              tabIndex={0}
              className={styles.slide}
              style={{ left: step === 1 ? '0' : step > 1 ? `${(step + 1) * -100}%` : `${(step + 1) * 100}%` }}
            >
              {isCollaborating && <GroupCollaborationForm onLoad={onLoad} />}
            </div>
          </div>

          <ModalBottomButtons
            closeButton={{
              callback: onClose,
            }}
            additionalLeftComponent={
              modalError ? (
                <div style={{ color: 'var(--color-error)' }}>{`${t('common.error')}: ${modalError}`}</div>
              ) : step === 0 ? (
                t('groups.personSelectHint')
              ) : undefined
            }
            submitButton={{
              disabled: Object.keys(errors).length > 0 || step !== maxSteps,
            }}
            additionalButtons={[
              {
                text: t('common.back'),
                icon: <ArrowLeftIcon className='small' />,
                disabled: step === 0,
                callback: () => {
                  setStep((prev) => prev - 1);
                  validateForm();
                },
              },
              {
                text: t('common.next'),
                icon: <ArrowRightIcon className='small' />,
                iconPosition: 'right',
                disabled: Object.keys(errors).length > 0 || step === maxSteps,
                callback: () => {
                  setStep((prev) => prev + 1);
                  validateForm();
                },
              },
            ]}
          />
        </Form>
      )}
    </Formik>
  );
};
